import { ZERO } from "./constants";
import { formatDateToYYYYMMDD } from "./formatters";

export type TDateRangeFilterTypes =
  | "allTime"
  | "today"
  | "yesterday"
  | "last7Days"
  | "oldestToNewest"
  | "newestToOldest"
  | "thisMonth"
  | "lastMonth"
  | "yearToDate";

const ONE = 1;
const SIX = 6;

export const parseCustomDateFilterRangeString = (
  query: string
): Record<string, string> => {
  const params = new URLSearchParams(query);
  const startDate = params.get("startDate");
  const endDate = params.get("endDate");

  return startDate ? { startDate, ...(endDate ? { endDate } : {}) } : {};
};

export const getTodayAsEndDateFilterValue = () => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endDate = new Date(today);
  return formatDateToYYYYMMDD(endDate);
};

export const getFilterDateRange = (payload: {
  filter: TDateRangeFilterTypes;
}): { startDate: string; endDate: string } => {
  const { filter } = payload;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  let startDate = new Date(today);
  let endDate = new Date(today);

  switch (filter) {
    case "allTime":
      break;
    case "today":
      break;
    case "yesterday":
      startDate.setDate(today.getDate() - ONE);
      endDate = new Date(startDate);
      break;
    case "last7Days":
      startDate = new Date(today);
      startDate.setDate(today.getDate() - SIX); // Full 7 days ago, **including today**
      break;
    case "thisMonth":
      startDate = new Date(today.getFullYear(), today.getMonth(), ONE);
      endDate = new Date(today);
      break;
    case "lastMonth":
      startDate = new Date(today.getFullYear(), today.getMonth() - ONE, ONE);
      endDate = new Date(today.getFullYear(), today.getMonth(), ZERO); // Last day of **last** month
      break;
    case "yearToDate":
      startDate = new Date(today.getFullYear(), ZERO, ONE); // **Jan 1st** of this year
      break;
    default:
      throw new Error("Invalid filter option");
  }

  return {
    startDate: formatDateToYYYYMMDD(startDate),
    endDate: formatDateToYYYYMMDD(endDate),
  };
};

export const getFilterFromDateRange = (payload: {
  startDate: string;
  endDate: string;
}): TDateRangeFilterTypes => {
  const { startDate, endDate } = payload;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const inputStart = new Date(startDate);
  const inputEnd = new Date(endDate);
  const todayStartDate = new Date(today);
  const todayEndDate = new Date(today);

  if (isNaN(inputStart.getDate()) || isNaN(inputEnd.getDate())) {
    throw new Error("Invalid date input");
  }

  if (
    inputStart.getDate() === todayStartDate.getDate() &&
    inputEnd.getDate() === todayEndDate.getDate()
  ) {
    return "today";
  }

  const yesterday = todayStartDate;
  yesterday.setDate(today.getDate() - ONE);
  if (
    inputStart.getDate() === yesterday.getDate() &&
    inputEnd.getDate() === yesterday.getDate()
  ) {
    return "yesterday";
  }

  const last7DaysStart = todayStartDate;
  last7DaysStart.setDate(today.getDate() - SIX);
  if (
    inputStart.getDate() === last7DaysStart.getDate() &&
    inputEnd.getDate() === todayEndDate.getDate()
  ) {
    return "last7Days";
  }

  const firstOfThisMonth = new Date(today.getFullYear(), today.getMonth(), ONE);
  if (
    inputStart.getMonth() === firstOfThisMonth.getMonth() &&
    inputEnd.getMonth() === todayEndDate.getMonth()
  ) {
    return "thisMonth";
  }

  const firstOfLastMonth = new Date(
    today.getFullYear(),
    today.getMonth() - ONE,
    ONE
  );
  const lastOfLastMonth = new Date(today.getFullYear(), today.getMonth(), ZERO);
  if (
    inputStart.getMonth() === firstOfLastMonth.getMonth() &&
    inputEnd.getMonth() === lastOfLastMonth.getMonth()
  ) {
    return "lastMonth";
  }

  const startOfYear = new Date(today.getFullYear(), ZERO, ONE);
  if (
    inputStart.getDate() === startOfYear.getDate() &&
    inputEnd.getDate() === todayEndDate.getDate()
  ) {
    return "yearToDate";
  }
  return "allTime";
};
